import { CSSProperties, Component, ReactNode } from 'react';
import { FormControl, Icon, InputLabel, MenuItem, Select, SelectChangeEvent, SelectProps, Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { colorToRGBA, sortByProperty } from '@synergy/utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import uniqid from 'uniqid';

import { PoolIconToggleButton } from '../icon-toggle-button/pool-icon-toggle-button';
import { SelectOption } from './models/select-option';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            textAlign: 'start',
            '& > div': {
                borderRadius: '4px',
            },
            '& svg': {
                top: 'calc(50% - 12px)',
                borderLeft: '1px solid ' + colorToRGBA(theme.palette.text.primary, 0.27),
                width: 24 * 2,
                height: 24,
                color: theme.palette.text.primary,
            },
        },
        selectRoot: {
            height: 'auto',
            minHeight: 24,
            padding: theme.spacing(1.5, 2),
        },
        disabled: {
            border: 'none',
            color: '#21272E',
            opacity: 0.6,
            '& > div': {
                border: '1px solid rgba(33, 39, 46, .27)',
                backgroundColor: theme.palette.background.default,
                '& span': {
                    color: '#21272E',
                    opacity: 0.6,
                },
            },
        },
        disabledLabel: {
            color: '#21272E',
            opacity: 0.6,
        },
        fullWidth: {
            display: 'flex',
        },
        simple: {
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '13px',

            '& svg': {
                border: 'none',
            },

            '& > div': {
                padding: theme.spacing(1.5, 4.5, 1.5, 3),
                '&:focus': {
                    borderRadius: theme.spacing(3),
                },
            },
        },
        selectSimple: {
            fontSize: '13px',
            padding: theme.spacing(1, 1.5),
        },
    });

// Option can be generic, but is not typeable because of static constructs
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OptionType = any;

interface PoolMultiSelectPropsBase extends WithStyles<typeof styles> {
    styleType?: keyof ReturnType<typeof styles>;
    fullWidth?: boolean;
    options: SelectOption[];
    selectStyles?: CSSProperties;
    label?: ReactNode;
    required?: boolean;
    onValueChange?: (value: OptionType) => void;
    noSorting?: boolean;
}

type PoolMultiSelectProps = PoolMultiSelectPropsBase & SelectProps;

interface PoolMultiSelectState {
    value: OptionType[];
    options: SelectOption[];
}

type ClassToClassMap = Partial<{
    [Class in keyof ReturnType<typeof styles>]: keyof ReturnType<typeof styles>;
}>;

export const PoolMultiSelect = withStyles(styles)(
    class PoolMultiSelectBase extends Component<PoolMultiSelectProps, PoolMultiSelectState> {
        private static baseClassToSelectClassMap: ClassToClassMap = {
            simple: 'selectSimple',
        };

        public override state: PoolMultiSelectState = {
            value: [],
            options: [],
        };

        private elementId = uniqid();

        private static getDisplayType(props: PoolMultiSelectProps): keyof ReturnType<typeof styles> {
            return props.styleType || 'root';
        }

        private static getClassNames(props: PoolMultiSelectProps) {
            return `${props.classes.root} ${props.classes[this.getDisplayType(props)]} ${
                props.disabled ? props.classes.disabled : ''
            } ${props.className || ''}`;
        }

        private static getSelectClassName(props: PoolMultiSelectProps): string {
            const classKey = this.baseClassToSelectClassMap[this.getDisplayType(props)];

            return classKey ? props.classes[classKey] : props.classes.selectRoot;
        }

        private renderLabel() {
            if (this.props.label) {
                return (
                    <InputLabel
                        htmlFor={this.elementId}
                        className={this.props.disabled ? this.props.classes.disabledLabel : ''}
                    >
                        {this.props.label}
                        {this.props.required && ' *'}
                    </InputLabel>
                );
            }

            return null;
        }

        private handleChangeNew = (event: SelectChangeEvent<unknown>) => {
            const value = (event.target.value as OptionType[]) ?? [];
            this.setState({ value });

            if (this.props.onValueChange) {
                // TODO: Check typing on "onValueChange", seems to be wrong
                this.props.onValueChange(value);
            }
        };

        private renderValue = (values: OptionType | OptionType[]) => {
            if (!values || values.length === 0) {
                return undefined;
            }

            const valueArray: OptionType[] = Array.isArray(values) ? values : [values];
            return valueArray
                .map(value => this.state.options.find(option => option.value === value)?.displayValue)
                .filter(value => !!value)
                .sort()
                .join(', ');
        };

        public override componentDidMount() {
            if (this.props.value !== undefined && this.props.value !== null) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                this.setState({ value: this.props.value as any[] });
            }
            this.updateOptions(this.props);
        }

        public override UNSAFE_componentWillReceiveProps(props: PoolMultiSelectProps) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let value: any[] = [];

            if (props.value !== null && props.value !== undefined) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value = props.value as any[];
            }

            this.setState({ value });

            if (this.props.options !== props.options) {
                this.updateOptions(props);
            }
        }

        public updateOptions = (props: PoolMultiSelectProps) => {
            this.setState({
                options: props.noSorting ? props.options : sortByProperty({ data: props.options, key: 'displayValue' }),
            });
        };

        public override render() {
            // Remove props which are not forwarded
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { styleType, classes, selectStyles, onValueChange, noSorting, ...baseSelectProps } = this.props;

            return (
                <FormControl
                    className={this.props.fullWidth ? this.props.classes.fullWidth : ''}
                    error={this.props.error}
                >
                    {this.renderLabel()}
                    <Select
                        {...baseSelectProps}
                        displayEmpty={true}
                        value={this.state.value}
                        onChange={this.handleChangeNew}
                        className={PoolMultiSelectBase.getClassNames(this.props)}
                        IconComponent={ExpandMoreIcon}
                        multiple={true}
                        renderValue={this.renderValue}
                    >
                        {this.state.options.map((option, index) => (
                            <MenuItem
                                key={this.elementId + index}
                                value={option.value}
                                className={PoolMultiSelectBase.getSelectClassName(this.props)}
                            >
                                <PoolIconToggleButton
                                    style={{ marginRight: 16 }}
                                    selected={this.state.value.includes(option.value)}
                                >
                                    <Icon className="icon-done" />
                                </PoolIconToggleButton>
                                <span>{option.displayValue}</span>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }
    }
);
